import { FC, createContext, useContext, useState, useCallback } from 'react';

type PopupsContextOpenType = 'proToPlus';
type PopupsContextOpens = {
  [currentType in PopupsContextOpenType]: boolean;
};

interface PopupsContextValue {
  opens: PopupsContextOpens;
  getIsOpenByType: (currentType: PopupsContextOpenType) => boolean;
  handleOpenByType: (currentType: PopupsContextOpenType) => void;
  handleCloseByType: (currentType: PopupsContextOpenType) => void;
}

const DEFAULTS_POPUPS_OPENS: PopupsContextOpens = {
  proToPlus: false,
};

const PopupsContext = createContext<PopupsContextValue>({
  opens: DEFAULTS_POPUPS_OPENS,
  getIsOpenByType: () => false,
  handleOpenByType: () => {},
  handleCloseByType: () => {},
});

export const usePopupsContext = (): PopupsContextValue =>
  useContext(PopupsContext);

const PopupsProvider: FC = ({ children }) => {
  const [opens, setOpens] = useState<PopupsContextOpens>(DEFAULTS_POPUPS_OPENS);

  const getIsOpenByType = useCallback(
    (currentType: PopupsContextOpenType) => opens[currentType],
    [opens],
  );

  const handleOpenByType = useCallback((currentType: PopupsContextOpenType) => {
    setOpens((prevOpens) => ({
      ...prevOpens,
      [currentType]: true,
    }));
  }, []);

  const handleCloseByType = useCallback(
    (currentType: PopupsContextOpenType) => {
      setOpens((prevOpens) => ({
        ...prevOpens,
        [currentType]: false,
      }));
    },
    [],
  );

  return (
    <PopupsContext.Provider
      value={{ opens, getIsOpenByType, handleOpenByType, handleCloseByType }}
    >
      {children}
    </PopupsContext.Provider>
  );
};

export default PopupsProvider;
